import React, { useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { visualizationSlice } from '../../../../Flux/Slice/Visualization/VisualizationSlice.js'
import type { SinkConfig, SinkId, SourceId } from '../../../../Flux/Slice/Visualization/VisualizationSliceTypes.js'
import { isLoadingState } from '../../../../Flux/Slice/Visualization/VisualizationSliceTypes.js'
import { kpiMode, useSourceView } from './UseSourceView.ts'
import { AreaChartPlot } from '../../../Component/Charts/AreaChartPlot.js'
import type { ChartRow } from '../../../Component/Charts/ChartRow.js'
import { nestedStackSeries, type StackSeries } from '../../../Component/Charts/StackSeries.js'
import { useTransformer } from '../Transformer/UseTransformer.tsx'
import { useSeriesSinkTable } from '../DataTable/UseSinkTable.ts'
import type { TaxaAggregationResult } from './Selector/TaxaAggregation.ts'
import type { TraitStackedBarSeries } from '../Transformer/Impl/TraitStackedBarSeries.ts'
import { withContinuousDateAxis } from './ContinuousDateAxis.js'

type AreaParams = { xLabel?: string }

const EMPTY_PATH: string[] = []
const EMPTY_ROWS: ChartRow[] = []
const EMPTY_KEYS: string[] = []
const EMPTY_SERIES: StackSeries[] = []

export default function AreaChartSink({ sourceId, config }: { sourceId: SourceId; sinkId: SinkId; config: SinkConfig<AreaParams> }) {
  const dispatch = useDispatch()

  const { snapshot, shared, status } = useSourceView(sourceId)

  const selectedPath = shared?.selectedPath ?? EMPTY_PATH
  const gradeThreshold = shared?.gradeThreshold ?? 0
  const countMode = shared?.countMode ?? 'Reads'
  const scalingMode = shared?.scalingMode ?? 'ABSOLUTE'
  const xAxisLabel = config.params?.xLabel ?? 'Groups'

  // Nature-KPI mode: compose by trait group instead of taxon (mirrors StackedBarSinkNext). The
  // trait transformer is only called in trait mode (ternary), so the taxa path is untouched.
  const { inTraitMode, traitGroup, noTraitMode } = kpiMode(shared)

  const aggRes = inTraitMode
    ? useTransformer(sourceId, undefined, 'traitStackedBarSeries', { selectedPath, gradeThreshold, traitGroup, scalingMode, noTraitMode })
    : useTransformer(sourceId, undefined, 'taxaAggregation', { selectedPath, gradeThreshold })

  const { rows, keys, stackSeries } = useMemo(() => {
    if (aggRes.status !== 'ready') {
      return { rows: EMPTY_ROWS, keys: EMPTY_KEYS, stackSeries: EMPTY_SERIES }
    }

    // Normalise both modes to AreaChartPlot's nested `{ xAxisvalue, data: { key: n } }` rows.
    // Trait mode arrives as flat `{ xAxisvalue, [trait]: n }` series.
    const { keys, chartRows } = inTraitMode
      ? (() => {
          const { data, stackKeys: traitKeys, sampleCounts } = aggRes.value as TraitStackedBarSeries
          return {
            keys: traitKeys,
            chartRows: data.map((row, i) => {
              const { xAxisvalue, ...rest } = row
              // `rest` is the taxon/trait map, which is exactly why the count travels as its own
              // field rather than on the row: spread here it would become a series.
              return { xAxisvalue: String(xAxisvalue), xAxisLabel, sampleCount: sampleCounts[i], data: rest as Record<string, number> }
            })
          }
        })()
      : (() => {
          const { taxa, rows: aggRows } = aggRes.value as TaxaAggregationResult
          return {
            keys: taxa,
            chartRows: withContinuousDateAxis(aggRows, taxa, xAxisLabel)
          }
        })()

    return { rows: chartRows as ChartRow[], keys, stackSeries: nestedStackSeries(keys) }
  }, [aggRes, xAxisLabel, inTraitMode])

  // Same rows the plot draws, handed to the container's "Numbers" view so the two cannot disagree.
  const tableRows = useMemo(
    () => rows.map((row) => ({ label: String(row.xAxisvalue ?? ''), sampleCount: row.sampleCount, values: row.data })),
    [rows]
  )

  useSeriesSinkTable({
    sourceId,
    selectedPath,
    gradeThreshold,
    inTraitMode,
    ready: aggRes.status === 'ready' && tableRows.length > 0,
    categories: keys,
    rows: tableRows,
    groupLabel: xAxisLabel,
    categoryLabel: inTraitMode ? 'Trait group' : 'Taxon',
    valueLabel: scalingMode === 'PERCENTAGE' ? 'Share of group (%)' : String(countMode)
  })

  const onAreaClick = (taxon: string) => {
    if (inTraitMode) return // trait groups aren't a drillable taxonomy level
    const nextPath = [...selectedPath, taxon]
    dispatch(visualizationSlice.actions.setTaxaZoom({ sourceId, path: nextPath }))
  }

  if (!snapshot) {
    return (
      <div className="text-sm opacity-70">
        Waiting for data… {isLoadingState(status) && <span className="loading loading-dots loading-xs" />}
      </div>
    )
  }

  if (aggRes.status === 'idle' || aggRes.status === 'loading') {
    return (
      <div className="text-sm opacity-70">Computing… {isLoadingState(status) && <span className="loading loading-dots loading-xs" />}</div>
    )
  }

  if (rows.length === 0 || stackSeries.length === 0) {
    return <div className="text-sm opacity-70">No data for current selection.</div>
  }

  return (
    <div className="flex flex-col gap-2 h-full" data-testid="area-chart-sink">
      <div className="text-xs opacity-70">Stacked Area Chart: Taxonomy by Sample</div>
      <AreaChartPlot
        data={rows}
        /* Aligned by index to `rows`, which are final here — withContinuousDateAxis has
           already inserted any empty intervals. */
        sampleCounts={rows.map((r) => r.sampleCount)}
        stackSeries={stackSeries}
        ylabel={String(countMode)}
        xlabel={xAxisLabel}
        onBarClick={onAreaClick}
        height={'100%'}
        hideCaption={true}
      />
    </div>
  )
}
